"use client";

import { motion } from "framer-motion";
import { Bot, ChefHat, Clock, Link2, Mic, SlidersHorizontal } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";
import { springConfig } from "@/lib/animations";

const APP_STORE_URL = "https://apps.apple.com/us/app/nombox-grocery-list/id6755436826";

const sectionVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig,
  },
};

interface FeatureCardProps {
  icon: ReactNode;
  title: string;
  description: string;
}

function FeatureCard({ icon, title, description }: FeatureCardProps) {
  return (
    <motion.div
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      className="flex flex-col items-center gap-3 rounded-2xl border border-border/50 bg-card/60 p-6 text-center backdrop-blur-sm"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
        {icon}
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
    </motion.div>
  );
}

interface FeatureSectionProps {
  eyebrow: string;
  title: string;
  description: string;
}

function FeatureSection({ eyebrow, title, description }: FeatureSectionProps) {
  return (
    <motion.div
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      className="py-6 md:py-0"
    >
      <span className="text-sm font-semibold uppercase tracking-wider text-primary">{eyebrow}</span>
      <h2 className="mt-2 text-2xl font-bold tracking-tight md:text-3xl">{title}</h2>
      <p className="mt-3 text-base leading-relaxed text-muted-foreground">{description}</p>
    </motion.div>
  );
}

export function WelcomeFeatures() {
  const t = useTranslations("welcome");

  return (
    <div className="relative z-10 bg-background">
      <div className="mx-auto px-6 pt-8 max-w-xl lg:grid lg:grid-cols-2 lg:gap-x-24 lg:gap-y-14 lg:px-16 lg:pt-24 lg:pb-12 lg:px-24">
        <FeatureSection
          eyebrow={t("smartListsEyebrow")}
          title={t("smartListsTitle")}
          description={t("smartListsDesc")}
        />

        <FeatureSection
          eyebrow={t("mealPlanningEyebrow")}
          title={t("mealPlanningTitle")}
          description={t("mealPlanningDesc")}
        />

        <FeatureSection
          eyebrow={t("recipesEyebrow")}
          title={t("recipesTitle")}
          description={t("recipesDesc")}
        />

        <FeatureSection
          eyebrow={t("discoverEyebrow")}
          title={t("discoverTitle")}
          description={t("discoverDesc")}
        />

        <FeatureSection
          eyebrow={t("togetherEyebrow")}
          title={t("togetherTitle")}
          description={t("togetherDesc")}
        />
      </div>

      {/* Highlights grid */}
      <div className="mx-auto px-3 py-8 md:px-16 md:py-16 lg:px-24">
        <motion.div
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="mb-8 text-center md:mb-10"
        >
          <h2 className="text-2xl font-bold tracking-tight md:text-3xl lg:text-4xl">
            {t("highlightsHeading")}
          </h2>
        </motion.div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <FeatureCard
            icon={<ChefHat className="h-6 w-6" />}
            title={t("dashboardTitle")}
            description={t("dashboardDesc")}
          />
          <FeatureCard
            icon={<Clock className="h-6 w-6" />}
            title={t("prepAheadTitle")}
            description={t("prepAheadDesc")}
          />
          <FeatureCard
            icon={<ChefHat className="h-6 w-6" />}
            title={t("sousChefTitle")}
            description={t("sousChefDesc")}
          />
          <FeatureCard
            icon={<Link2 className="h-6 w-6" />}
            title={t("urlImportTitle")}
            description={t("urlImportDesc")}
          />
          <FeatureCard
            icon={<SlidersHorizontal className="h-6 w-6" />}
            title={t("scalingTitle")}
            description={t("scalingDesc")}
          />
          <FeatureCard
            icon={<Mic className="h-6 w-6" />}
            title={t("voiceInputTitle")}
            description={t("voiceInputDesc")}
          />
        </div>
      </div>

      {/* Agents */}
      <div className="mx-auto max-w-lg px-3 pb-8 md:px-16">
        <motion.section
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          aria-labelledby="welcome-agents-title"
          className="flex flex-col items-center gap-4 rounded-2xl border border-border/50 bg-card/60 p-6 text-center backdrop-blur-sm md:p-10"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Bot className="h-6 w-6" aria-hidden />
          </div>
          <span className="text-sm font-semibold uppercase tracking-wider text-primary">
            {t("agentsEyebrow")}
          </span>
          <h2 id="welcome-agents-title" className="text-2xl font-bold tracking-tight md:text-3xl">
            {t("agentsTitle")}
          </h2>
          <p className="max-w-md text-base leading-relaxed text-muted-foreground">
            {t("agentsDesc")}
          </p>
          <Button variant="outline" size="lg" className="h-[50px]" asChild>
            <Link href="/agents">{t("agentsCta")}</Link>
          </Button>
        </motion.section>
      </div>

      {/* Closing CTA */}
      <div className="mx-auto px-3">
        <motion.section
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="flex flex-col items-center gap-5 py-12 pb-20 text-center md:py-16 md:pb-28"
        >
          <h2 className="text-2xl font-bold tracking-tight md:text-3xl lg:text-4xl">
            {t("ctaHeading")}
          </h2>
          <p className="max-w-md text-base leading-relaxed text-muted-foreground">{t("ctaDesc")}</p>
          <div className="flex items-center gap-3 sm:flex-row sm:gap-4">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 transition-opacity hover:opacity-80"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/badges/download-on-the-app-store.svg"
                alt={t("ctaBadgeAlt")}
                width={150}
                height={50}
                className="h-[50px] w-auto"
              />
            </a>
            <Button variant="default" size="lg" className="h-[50px] shrink-0" asChild>
              <Link href="/auth/signin">{t("ctaButton")}</Link>
            </Button>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
